import { useIntl } from 'react-intl';
import { Button, Checkbox, Dropdown, Icon, Table } from '../../../../ui';
import { InfrastructureProps } from '../../../../interfaces/BackendModels';
import { MenuProps, message } from 'antd';
import { StyledBusinessTab } from '../style';
import React, { useMemo, useState } from 'react';
import useQueryApiClient from 'utils/useQueryApiClient';
import { CreateEditInfrastructure } from './CreateEditInfrastructure/CreateEditInfrastructure';
import { OpenHoursInput, normalizeOpenHours } from 'utils/timeHelpers';
import { permissions } from '../../../../config/config';
import { useAuth } from '../../../../hooks/useAuth';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';

interface BusinessAddressesProps {
  farmId?: number;
  onInfrastructureChanged?: () => void;
}

export const BusinessAddresses: React.FC<BusinessAddressesProps> = ({
  farmId,
  onInfrastructureChanged,
}) => {
  const [isOpenEditCreateDrawer, setIsOpenEditCreateDrawer] =
    useState<boolean>(false);
  const [editInfrastructureId, setEditInfrastructureId] = useState<
    number | null
  >(null);
  const [reloadCount, setReloadCount] = useState<number>(0);
  const intl = useIntl();

  const { can } = useAuth();

  const stableFilter = useMemo(() => {
    return farmId ? { farmId } : undefined;
  }, [farmId]);

  const { appendData: deleteInfrastructure } = useQueryApiClient({
    request: {
      url: 'api/v2/farm/infrastructures/:id',
      method: 'DELETE',
    },
    onSuccess: () => {
      setReloadCount((prev: number) => prev + 1);
      message.success(
        intl.formatMessage({ id: 'message.sucess_infrastructure_deleted' })
      );
      onInfrastructureChanged?.();
    },
  });

  const handleEdit = (record: InfrastructureProps) => {
    setIsOpenEditCreateDrawer(true);
    setEditInfrastructureId(record.id);
  };
  const handleDelete = (id: number) => {
    deleteInfrastructure([], { id: id });
  };
  const handleCloseDrawer = () => {
    setIsOpenEditCreateDrawer(false);
    setEditInfrastructureId(null);
  };

  const handleOpenEditCreateDrawer = () => {
    setIsOpenEditCreateDrawer(true);
  };

  const formatOpenDays = (open_hours: OpenHoursInput) => {
    if (!open_hours) {
      return '';
    }
    const latvianDays = ['P.', 'O.', 'T.', 'C.', 'P.', 'S.', 'S.'];
    return normalizeOpenHours(open_hours)
      .map((day, index) => (!day || day.closed ? '' : latvianDays[index]))
      .filter(Boolean)
      .join(', ');
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'delivery.address' }),
      dataIndex: 'address_pak',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'general.infrastructure' }),
      dataIndex: 'structural_type',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'general.working_hours' }),
      render: (record: InfrastructureProps) => {
        const text = formatOpenDays(record.open_hours);
        return <span className={'open-hours'}>{text}</span>;
      },
    },
    {
      title: intl.formatMessage({ id: 'general.statuss' }),
      render: (record: InfrastructureProps) => {
        return <Checkbox checked={record.status} disabled={true} />;
      },
      className: 'centered-column',
    },
    {
      title: intl.formatMessage({ id: 'farms.show_on_map' }),
      render: (record: InfrastructureProps) => {
        return <Checkbox checked={record.show_on_map} disabled={true} />;
      },
      className: 'centered-column',
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      render: (record: InfrastructureProps) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: intl.formatMessage({ id: 'general.edit' }),
            onClick: () => handleEdit(record),
          },
          can(permissions.view_company)
            ? {
                key: 'delete',
                label: intl.formatMessage({ id: 'general.delete' }),
                onClick: () => {
                  handleDelete(record.id);
                },
                disabled: !record.editable,
              }
            : null,
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']} key={record.id}>
            <Button
              label={intl.formatMessage({ id: 'general.actions' })}
              icon={<Icon name={'arrow-down-black'} type={'any'} />}
              iconPosition={'end'}
            />
          </Dropdown>
        );
      },
    },
  ];

  const mobileItems = {
    coverFields: [
      {
        name: '_',
        render: () => {
          return intl.formatMessage({ id: 'farms.object_name' });
        },
      },
      {
        name: 'object_name',
        render: (value: string) => value,
      },
    ],
    bodyFields: [
      {
        name: 'address_pak',
        label: intl.formatMessage({ id: 'delivery.address' }),
        render: (value: string) => value,
      },
      {
        name: 'status',
        label: intl.formatMessage({ id: 'general.statuss' }),
        render: (value: boolean, record: InfrastructureProps) => {
          return <Checkbox checked={record.status} disabled={true} />;
        },
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (value: undefined, record: InfrastructureProps) => {
          const items: MenuProps['items'] = [
            {
              key: 'edit',
              label: intl.formatMessage({ id: 'general.edit' }),
              onClick: () => handleEdit(record),
            },
            {
              key: 'delete',
              label: intl.formatMessage({ id: 'general.delete' }),
              onClick: () => {
                handleDelete(record.id);
              },
              disabled: !record.editable,
            },
          ];

          return (
            <Dropdown menu={{ items }} trigger={['click']} key={record.id}>
              <Button
                label={intl.formatMessage({ id: 'general.actions' })}
                icon={<Icon name={'arrow-down-black'} type={'any'} />}
                iconPosition={'end'}
              />
            </Dropdown>
          );
        },
      },
    ],
  };

  return (
    <StyledBusinessTab>
      <Button
        label={intl.formatMessage({ id: 'general.add_address' })}
        type={'primary'}
        className={'new-btn'}
        onClick={handleOpenEditCreateDrawer}
        ability={permissions.view_company}
      />
      <ResponsiveWrapper
        desktopComponent={
          <Table
            url={'/api/v2/farm/infrastructures'}
            columns={columns}
            reload={reloadCount}
            filter={stableFilter}
          />
        }
        mobileComponent={
          <MobileList
            url={'/api/v2/farm/infrastructures'}
            bodyFields={mobileItems.bodyFields}
            coverFields={mobileItems.coverFields}
            reload={reloadCount}
            filter={stableFilter}
            footerFields={mobileItems.footerFields}
          />
        }
      />
      <CreateEditInfrastructure
        open={isOpenEditCreateDrawer}
        editInfrastructureId={editInfrastructureId}
        setReloadCount={setReloadCount}
        onClose={handleCloseDrawer}
        farmId={farmId}
        onAfterSave={onInfrastructureChanged}
      />
    </StyledBusinessTab>
  );
};
